import { useEffect } from "react";
import { getPendingRequestCount, onPendingRequestsChange } from "../../utility/WebService";
import "./GlobalSubmitGuard.css";

/**
 * Application-wide double-submit guard.
 *
 * Mounted once at the app root. Uses a single capture-phase `click` listener on
 * <document>, so it covers every Save / Submit button in the app — in forms,
 * modals and anywhere else — with zero per-screen changes.
 *
 * How it works:
 *   - The FIRST click on a Save/Submit button is allowed through, then the
 *     button is "armed" (marked with `data-submit-armed`, styled as busy).
 *   - Any further click on that button is swallowed (preventDefault +
 *     stopImmediatePropagation) until the button is disarmed, so the handler /
 *     form submit cannot run twice.
 *   - The button is disarmed when the API request the click triggered settles
 *     (tracked app-wide in WebService). If the click started no request within
 *     a short grace window (e.g. a synchronous validation failure), it is
 *     disarmed then. A hard cap guarantees it never stays stuck.
 *
 * A guarded button is a <button> / <input type=submit> whose visible text is
 * (or starts with) one of GUARDED_LABELS ("Save", "Submit", "Update", "Send",
 * "Confirm", "Proceed", "Continue", "Process", "Approve", "Reject", "Pay",
 * "Delete", "Export", "Download", "Apply", "Add", "Create", "Generate") — this
 * covers "Save draft", "Submit and Execute", "Send Code", "Generate & Download",
 * etc. Note: `<a>`/NavLink "Add" links that only navigate are NOT matched
 * (selector is button / input[submit]). Opt out with `data-no-submit-guard` on
 * the button or any ancestor.
 *
 * The guard only blocks REPEAT clicks; it never disables a button before its
 * first click, and only uses an inert data-attribute (not `disabled`, which
 * mid-dispatch can cancel a form submit), so it cannot suppress a real submit.
 */

// If no request starts within this window after the click, treat it as a
// non-network action (e.g. failed validation) and re-enable the button.
const GRACE_NO_REQUEST_MS = 700;
// After the in-flight count returns to baseline, wait briefly to absorb a
// chained follow-up request before disarming.
const SETTLE_DELAY_MS = 150;
// Absolute ceiling — the button is always re-enabled after this, no matter what.
const HARD_CAP_MS = 30000;

// Action words whose buttons should be guarded against a duplicate click. A
// button matches when its visible label equals one of these or starts with it
// followed by a space (so "Save draft", "Submit and Execute", "Send Code",
// "Update Details", "Confirm & Continue" all match).
const GUARDED_LABELS = ["save", "submit", "update", "send", "confirm", "proceed", "continue", "process", "approve", "reject", "pay", "delete", "export", "download", "apply", "add", "create", "generate"];

const isGuardedButton = (btn: HTMLElement): boolean => {
  if (btn.hasAttribute("data-no-submit-guard")) return false;
  if (btn.closest("[data-no-submit-guard]")) return false;
  const label = (btn.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!label) return false;
  return GUARDED_LABELS.some((w) => label === w || label.startsWith(w + " "));
};

const GlobalSubmitGuard = () => {
  useEffect(() => {
    // Each armed button maps to the function that disarms it.
    const armed = new Map<HTMLElement, () => void>();

    const arm = (btn: HTMLElement) => {
      if (armed.has(btn)) return;
      btn.setAttribute("data-submit-armed", "");

      // Requests already in flight (unrelated background calls) form the
      // baseline; "our" request is anything above it.
      const baseline = getPendingRequestCount();
      let sawRequest = false;
      let finished = false;
      const cleanups: Array<() => void> = [];

      const finish = () => {
        if (finished) return;
        finished = true;
        btn.removeAttribute("data-submit-armed");
        armed.delete(btn);
        cleanups.forEach((f) => { try { f(); } catch (e) { /* noop */ } });
      };
      armed.set(btn, finish);

      const off = onPendingRequestsChange((count) => {
        if (count > baseline) sawRequest = true;
        if (sawRequest && count <= baseline) {
          window.setTimeout(() => {
            if (getPendingRequestCount() <= baseline) finish();
          }, SETTLE_DELAY_MS);
        }
      });
      cleanups.push(off);

      const grace = window.setTimeout(() => {
        if (!sawRequest) finish();
      }, GRACE_NO_REQUEST_MS);
      cleanups.push(() => window.clearTimeout(grace));

      const hard = window.setTimeout(finish, HARD_CAP_MS);
      cleanups.push(() => window.clearTimeout(hard));
    };

    const onClickCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== "function") return;
      const btn = target.closest("button, input[type=submit]") as HTMLElement | null;
      if (!btn) return;
      if ((btn as HTMLButtonElement).disabled) return;
      if (!isGuardedButton(btn)) return;

      if (btn.hasAttribute("data-submit-armed")) {
        // A repeat click while the first is still being processed — swallow it
        // so neither the onClick handler nor an implicit form submit can run.
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      // First click: let it proceed as normal, then arm against repeats.
      arm(btn);
    };

    document.addEventListener("click", onClickCapture, true);
    return () => {
      document.removeEventListener("click", onClickCapture, true);
      armed.forEach((disarm) => disarm());
      armed.clear();
    };
  }, []);

  return null;
};

export default GlobalSubmitGuard;
